<script setup lang="ts">
import type { AssignableRole, ResourceAccessType } from '@/modules/IdentityAndAccess/types'
import { useWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import { useWorkspaceMembers } from '@/modules/WorkspaceSettings/useWorkspaceMembers'
import { toast } from '@/shared/toast'
import { invariant } from '@/shared/utils/typeAssertions'
import { FOLDER_NAME_FALLBACK } from '../constants'
import type { FolderInTree } from '../Folders/types'
import { useFolderMembersStore } from '../Folders/useFolderMembers'
import PermissionsDialog from './PermissionsDialog.vue'

const props = defineProps<{
  open: boolean
  folder: Pick<FolderInTree, 'id' | 'name'>
}>()

defineEmits<{ (e: 'close'): void }>()

const { workspaceId } = useWorkspaceContext()
const folderMembersStore = useFolderMembersStore()
const workspaceMembersStore = useWorkspaceMembers()

const updateFolderAccessType = async (accessType: ResourceAccessType) => {
  const res = await folderMembersStore.setDefaultAccess({
    accessType,
    folderId: props.folder.id,
    workspaceId: workspaceId.value,
  })

  if (!res.ok) {
    toast.error('Failed to update folder access type')
  }
}

const onChangeRole = async (userId: string, role: AssignableRole | null) => {
  if (role) {
    const res = await folderMembersStore.updateMemberRole({
      folderId: props.folder.id,
      workspaceId: workspaceId.value,
      userId,
      role,
    })

    if (!res.ok) {
      toast.error('Failed to update role')
    }
  } else {
    const res = await folderMembersStore.deleteMember({
      folderId: props.folder.id,
      workspaceId: workspaceId.value,
      userId,
    })

    if (!res.ok) {
      toast.error('Failed to reset role')
    }
  }
}

const onInviteUsers = async (userIds: string[], role: AssignableRole) => {
  let failedNames: string[] = []
  await Promise.all(
    userIds.map(async (userId) => {
      const workspaceMember = workspaceMembersStore.getMember(userId)
      invariant(workspaceMember, 'User is not a member of this workspace')

      const res = await folderMembersStore.addMember({
        folderId: props.folder.id,
        workspaceId: workspaceId.value,
        userId,
        role,
      })

      if (!res.ok) {
        failedNames.push(workspaceMembersStore.getMemberNameFromId(userId))
      }
    }),
  )

  if (failedNames.length === userIds.length) {
    toast.error('Failed to invite users')
  } else if (failedNames.length > 0) {
    toast.error(`Failed to invite users: ${failedNames.join(', ')}`)
  }
}
</script>

<template>
  <PermissionsDialog
    :root-project-id="'remove'"
    :heading="{
      icon: 'folder',
      title: folder.name || FOLDER_NAME_FALLBACK,
    }"
    :open="open"
    resource-type="folder"
    :access-type="folderMembersStore.defaultAccessType"
    :members="folderMembersStore.folderMembers"
    @change:access-type="updateFolderAccessType"
    @change:user-role="onChangeRole"
    @invite="onInviteUsers"
    @close="$emit('close')"
  />
</template>
